import React from "react";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import ReactMarkdown from "react-markdown";
import { Tables } from "@/types/database.types";
import { parseRichContent } from "@/utils/richContentParser";
import { RichContentRenderer } from "./RichContentRenderer";
import { removeSourcesFooter } from "@/utils/messageFilters";

interface ChatMessageBubbleProps {
  content: string;
  type: "user" | "agent" | "system";
  agent?: Tables<"agents">;
  isLoading?: boolean;
  brandColor?: string;
  organization?: Tables<"organizations">;
  textColor?: string;
  onCloseConversation?: () => void;
  onSendMessage?: (message: string) => void;
}

const ChatMessageBubble: React.FC<ChatMessageBubbleProps> = ({
  content,
  type,
  agent,
  organization,
  isLoading = false,
  brandColor = "#060520",
  //textColor = "#FFFFFF",
  onCloseConversation,
  onSendMessage,
}) => {
  if (isLoading) {
    return (
      <div className="flex items-start gap-2 animate-pulse">
        <div className="relative">
          <Avatar className="h-[50px] w-[50px]">
            <AvatarFallback>
              {agent?.agent_name?.substring(0, 2) ||
                agent?.title?.substring(0, 2) ||
                "AI"}
            </AvatarFallback>
          </Avatar>
          {/* Green online status dot */}
          <div
            className="absolute bottom-0 right-0 w-3 h-3 rounded-full border-2 border-white"
            style={{ backgroundColor: "#059669" }}
          />
        </div>
        <div className="bg-muted h-6 w-24 rounded-md"></div>
      </div>
    );
  }

  if (type === "system") {
    return (
      <div className="text-center text-sm text-muted-foreground py-2">
        {content}
      </div>
    );
  }

  if (type === "user") {
    return (
      <div className="flex justify-end">
        <div
          className="px-4 py-3 max-w-[80%]"
          style={{
            backgroundColor: brandColor || "#060520",
            color: "#FFFFFF",
            borderRadius: "14px",
            boxShadow: "1px 1px 3px rgba(0, 0, 0, 0.08)",
          }}
        >
          {content}
        </div>
      </div>
    );
  }

  // For agent messages, filter out source references first
  const filteredContent = removeSourcesFooter(content);

  // Then process rich content
  const processedContent = parseRichContent(filteredContent);

  // First check for agent avatar, then fall back to organization logo
  // (reversing the current priority)
  const avatarUrl = agent?.avatar_url || organization?.logo_url;

  // Check if content contains the special "<b>Submit & Close</b>" marker (backward compatibility)
  const hasSubmitCloseButton = filteredContent.includes(
    "<b>Submit & Close</b>",
  );

  // Remove the button marker from the content that will be displayed (backward compatibility)
  const displayContent = hasSubmitCloseButton
    ? processedContent.cleanContent.replace(/<b>Submit & Close<\/b>/g, "")
    : processedContent.cleanContent;

  return (
    <div className="mb-6">
      {/* Message bubble with avatar */}
      <div className="flex items-start gap-2">
        <div className="relative">
          <Avatar className="h-[50px] w-[50px]">
            {avatarUrl ? (
              <AvatarImage
                src={avatarUrl}
                alt={agent?.agent_name || agent?.title || "AI"}
                className="object-contain"
              />
            ) : (
              <AvatarFallback>
                {(agent?.agent_name || agent?.title || "AI").substring(0, 2)}
              </AvatarFallback>
            )}
          </Avatar>
          {/* Green online status dot */}
          <div
            className="absolute bottom-0 right-0 w-3 h-3 rounded-full border-2 border-white"
            style={{ backgroundColor: "#059669" }}
          />
        </div>
        <div
          className="bg-white p-3 max-w-[80%] text-black prose prose-sm border-none"
          style={{
            borderRadius: "14px",
            boxShadow: "1px 1px 3px rgba(0, 0, 0, 0.08)",
          }}
        >
          {/* Markdown content */}
          <ReactMarkdown
            components={{
              a: ({ node, ...props }) => (
                <a {...props} target="_blank" rel="noopener noreferrer" />
              ),
            }}
          >
            {displayContent}
          </ReactMarkdown>

          {/* Legacy Submit & Close button */}
          {hasSubmitCloseButton && onCloseConversation && (
            <button
              onClick={onCloseConversation}
              className="mt-3 px-4 py-2 rounded-md font-medium text-white"
              style={{ backgroundColor: brandColor }}
            >
              Submit & Close
            </button>
          )}

          {/* Render buttons inside the chat bubble */}
          {processedContent.insideContent.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-3">
              {processedContent.insideContent.map((item, index) => (
                <RichContentRenderer
                  key={`inside-${index}`}
                  content={item}
                  onButtonClick={(text) => onSendMessage?.(text)}
                  brandColor={brandColor}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Render content that should appear below the bubble */}
      {processedContent.belowContent.length > 0 && (
        <div className="ml-10 mt-2">
          {processedContent.belowContent.map((item, index) => (
            <RichContentRenderer
              key={`below-${index}`}
              content={item}
              onButtonClick={(text) => onSendMessage?.(text)}
              brandColor={brandColor}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default ChatMessageBubble;
